<template>
	<v-dialog v-model="createPaymentModal" transition="scroll-x-reverse-transition" max-width="600">
		<template #activator="{ on, attrs }">
			<v-list-item v-if="listItem"
				color="success"
				v-bind="attrs"
				v-on="on">
				<v-list-item-icon><v-icon color="green">mdi-currency-usd</v-icon></v-list-item-icon>
				<v-list-item-content class="green--text text--darken-2"><slot /></v-list-item-content>
			</v-list-item>
			<v-btn v-else
				color="success"
				small
				elevation="0"
				v-bind="attrs"
				v-on="on">
				<v-icon left>mdi-currency-usd</v-icon>
				Add Payment
			</v-btn>
		</template>
		<v-card>
			<v-card-title>
				<div>Create Payment</div>
				<v-icon right class="ml-auto theme--dark" @click="createPaymentModal = false">mdi-close</v-icon>
			</v-card-title>
			<v-divider class="mb-5" />
			<v-card-text>
				<v-alert v-if="noInvoices" color="red lighten-4">
					<strong>Oops!</strong> There are no invoices available, you must create an invoice before inserting a payment.
				</v-alert>
				<v-form @submit="createPayment">
					<v-row>
						<v-col>
							<v-select
								v-model="form.invoice_id"
								:items="invoicesSelect"
								:disabled="noInvoices"
								:error="hasErrors(form, 'invoice_id')"
								:hide-details="! hasErrors(form, 'invoice_id')"
								:error-messages="errorMsg(form, 'invoice_id')"
								outlined
								name="invoice_id"
								label="Invoice ID"
								dense>
								<template #item="{ item }">
									<div class="py-2">
										<div><strong>{{ item.text }}</strong></div>
										<div class="font-mono">{{ item.total | money }}</div>
									</div>
									<div class="ml-auto">
										<v-chip v-if="item.status"
											label
											dark
											:color="item.status.color"
											small>
											{{ item.status.name }}
										</v-chip>
									</div>
								</template>
							</v-select>
						</v-col>
					</v-row>
					<v-row>
						<v-col>
							<v-text-field
								v-model="form.amount"
								type="number"
								name="amount"
								:error="hasErrors(form, 'amount')"
								:hide-details="! hasErrors(form, 'amount')"
								:error-messages="errorMsg(form, 'amount')"
								autofocus
								label="Payment Amount"
								required
								persistent-placeholder
								outlined
								prepend-inner-icon="mdi-currency-usd"
								placeholder="0.00"
								dense
								@input="amountChanged = true"></v-text-field>
						</v-col>
						<v-col>
							<v-text-field
								v-model="form.transaction_id"
								name="transaction_id"
								:error="hasErrors(form, 'transaction_id')"
								:hide-details="! hasErrors(form, 'transaction_id')"
								:error-messages="errorMsg(form, 'transaction_id')"
								label="Transaction ID / Check #"
								required
								persistent-placeholder
								outlined
								placeholder="(optional)"
								dense></v-text-field>
						</v-col>
					</v-row>
					<v-row>
						<v-col cols="4">
							<v-select
								v-model="form.type"
								:items="paymentTypes"
								:disabled="noInvoices"
								:error="hasErrors(form, 'type')"
								:hide-details="! hasErrors(form, 'type')"
								:error-messages="errorMsg(form, 'type')"
								outlined
								persistent-placeholder
								name="type"
								label="Payment Type"
								dense />
						</v-col>
						<v-col>
							<v-menu
								v-model="datePicker"
								:close-on-content-click="false"
								transition="scale-transition"
								offset-y
								min-width="auto">
								<template #activator="{ on, attrs }">
									<v-text-field
										:value="form.paid_on | localDate"
										label="Payment Date"
										hint="Select a date this payment was received. Default is today."
										placeholder="(optional) Today"
										append-icon="mdi-calendar"
										:error="hasErrors(form, 'paid_on')"
										:error-messages="errorMsg(form, 'paid_on')"
										persistent-hint
										persistent-placeholder
										readonly
										outlined
										clearable
										dense
										v-bind="attrs"
										v-on="on"></v-text-field>
								</template>
								<v-date-picker
									v-model="form.paid_on"
									no-title
									scrollable
									class="pb-4"
									@input="datePicker = false"></v-date-picker>
							</v-menu>
						</v-col>
					</v-row>
					<v-row class="mt-0 ml-1">
						<v-col class="pa-0">
							<v-checkbox
								v-model="form.complete_project"
								hide-details
								label="Update project status as &quot;Done&quot;." />
						</v-col>
					</v-row>
				</v-form>
			</v-card-text>
			<v-divider />
			<v-card-actions>
				<v-btn elevation="0"
					rounded
					color="success"
					:disabled="! form.invoice_id"
					:loading="form.processing"
					@click="createPayment">
					Create Payment
				</v-btn>
				<v-btn text color="red darken-2" @click="resetForm">Cancel</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>
<script>
import Backend from '@/Layouts/Backend'

export default {
	components: {},
	layout: Backend,
	props: {
		listItem: {
			type: Boolean,
			default: false
		},
		invoiceId: {
			type: Number,
			default: () => null
		}
	},
	data () {
		return {
			createPaymentModal: false,
			invoices: [],
			invoice: {},
			invoicesSelect: [],
			datePicker: false,
			amountChanged: false,
			paymentTypes: [
				{ text: 'Wire', value: 'wire' },
				{ text: 'Check', value: 'check' }
			],
			form: this.$inertia.form({
				type: 'wire',
				invoice_id: null,
				amount: null,
				transaction_id: null,
				complete_project: 0
			})
		}
	},
	computed: {
		noInvoices () {
			return (! this.invoice && ! (this.length(this.invoices))
				|| this.invoices.hasOwnProperty('data') && !this.length(this.invoices.data))
		}
	},
	watch: {
		createPaymentModal (newValue) {
			if (newValue) {
				this.getInvoices()
			}
		},
		'form.invoice_id' (newValue) {
			if (newValue && !this.amountChanged) {
				this.setInvoiceAmount()
			}
		}
	},
	methods: {
		/**
		 * User has selected an invoice or an invoice was set automatically.
		 * If no payment amount was entered, we can pre-populate this to the invoice total.
		 */
		setInvoiceAmount () {
			const theInvoice = _.find(this.invoicesSelect, { value: this.form.invoice_id })

			if (theInvoice && theInvoice.hasOwnProperty('total')) {
				this.$set(this.form, 'amount', theInvoice.total_due)
			}
		},

		getInvoices () {
			this.$inertia.reload({
				only: ['invoice', 'invoices'],
				onBefore: () => this.invoicesSelect = [],
				onSuccess: () => {
					this.invoice = this.$page.props.invoice || {}
					this.invoices = this.$page.props.invoices || []

					if (this.noInvoices) {
						return
					}

					if(this.invoice && this.invoice.hasOwnProperty('id')) {
						this.invoicesSelect.push({
							text: 'Invoice #' + this.invoice.id,
							value: this.invoice.id,
							total: this.invoice.total,
							total_due: this.invoice.total_due,
							status: this.invoice.status
						})
					}

					if(this.length(this.invoices) && this.length(this.invoices.data)) {
						for (const invoiceKey in this.invoices.data) {
							if (!this.invoices.data.hasOwnProperty(invoiceKey)) continue
							const theInvoice = this.invoices.data[invoiceKey]
							this.invoicesSelect.push({
								text: 'Invoice #' + theInvoice.id,
								value: theInvoice.id,
								total: theInvoice.total,
								total_due: theInvoice.total_due,
								status: theInvoice.status
							})
						}
					}

					if (this.invoiceId) {
						this.$set(this.form, 'invoice_id', this.invoiceId)
					} else if (this.length(this.invoicesSelect) >= 1) {
						this.$set(this.form, 'invoice_id', this.invoicesSelect[0].value)
					}
				}
			})
		},

		createPayment () {
			const route = this.route('payments.store')
			this.form.post(route, {
				only: ['invoice', 'invoices', 'flash'],
				onFinish: () => {
					this.resetForm()
					this.$emit('success')
				}
			})
		},

		resetForm () {
			this.amountChanged = false
			this.createPaymentModal = false
			this.form.reset()
		}
	}
}
</script>
